// components/Payroll/PayrollRunList.tsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
} from "@mui/material";
// import { PlayArrow, Visibility, Receipt } from '@mui/icons-material';
import { PlayArrow, Visibility } from "@mui/icons-material";
import { usePayroll } from "../../contexts/PayrollContext";

const PayrollRunList: React.FC = () => {
  const { payrollRuns, runPayroll, processPayroll, loadPayrollRuns, loading } =
    usePayroll();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadPayrollRuns();
  }, [loadPayrollRuns]);

  const handleRunPayroll = async () => {
    setError("");
    setSuccess("");

    try {
      const today = new Date();
      const payPeriodEnd = new Date(today);
      const payPeriodStart = new Date(today);
      payPeriodStart.setDate(payPeriodStart.getDate() - 14); // 2 weeks ago

      await runPayroll(
        payPeriodStart.toISOString().split("T")[0],
        payPeriodEnd.toISOString().split("T")[0]
      );
      setSuccess("Payroll run started successfully!");
    } catch (err) {
      setError("Failed to run payroll. Please try again.");
      console.error("Error running payroll:", err);
    }
  };

  const handleProcessPayroll = async (payrollRunId: string) => {
    try {
      await processPayroll(payrollRunId);
      setSuccess("Payroll processed successfully!");
    } catch (err) {
      setError("Failed to process payroll. Please try again.");
      console.error("Error processing payroll:", err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PROCESSING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (loading) {
    return <Typography>Loading payroll runs...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Payroll Runs</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayArrow />}
          onClick={handleRunPayroll}
          disabled={loading}
        >
          {loading ? "Running..." : "Run New Payroll"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="textSecondary" gutterBottom>
                Total Payroll Runs
              </Typography>
              <Typography variant="h4">{payrollRuns.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* Add more stat cards as needed */}
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pay Period</TableCell>
              <TableCell>Pay Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Total Gross</TableCell>
              <TableCell>Total Net</TableCell>
              <TableCell>Total Taxes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrollRuns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 4 }}>
                    No payroll runs found. Click "Run New Payroll" to get
                    started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              payrollRuns.map((payroll) => (
                <TableRow key={payroll.id}>
                  <TableCell>
                    {payroll.payPeriodStart} to {payroll.payPeriodEnd}
                  </TableCell>
                  <TableCell>{payroll.payDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={payroll.status}
                      color={getStatusColor(payroll.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(payroll.totalGross)}</TableCell>
                  <TableCell>{formatCurrency(payroll.totalNet)}</TableCell>
                  <TableCell>{formatCurrency(payroll.totalTaxes)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      {payroll.status === "DRAFT" && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleProcessPayroll(payroll.id)}
                        >
                          Process
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PayrollRunList;
